import React, { useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import UsersFilters from "../components/Users/UsersFilters";
import UsersTable from "../components/Users/UsersTable";
import PeopleIcon from "../assets/images/hr_rgb_red.svg";
import PaginationComponent from "../components/PaginationComponent";
import UsersButtons from "../components/Users/UsersButtons";
import "../assets/styles/users.scss";
import api from "../services/httpClient";

function normalizeBranch(branch) {
  if (!branch) return null;

  if (typeof branch === "string") {
    return { id: branch, name: branch };
  }

  if (typeof branch === "object") {
    const id = branch.id ?? branch.Id ?? branch.name ?? branch.Name;
    const name = branch.name ?? branch.Name ?? String(id ?? "");
    if (!name) return null;
    return { ...branch, id, name };
  }

  return null;
}

function aggregateUsers(items) {
  const map = new Map();

  (Array.isArray(items) ? items : []).forEach((item, index) => {
    const rawBranches = Array.isArray(item?.branches) ? item.branches : [];
    const normalizedBranches = rawBranches.map(normalizeBranch).filter(Boolean);
    const key = item?.id ?? `${item?.login || ""}-${item?.email || ""}-${index}`;

    if (!map.has(key)) {
      map.set(key, {
        ...item,
        branches: normalizedBranches,
      });
      return;
    }

    const existing = map.get(key);
    const mergedBranches = [
      ...(Array.isArray(existing.branches) ? existing.branches : []),
      ...normalizedBranches,
    ];
    const uniqueBranches = [];
    const seen = new Set();

    mergedBranches.forEach((branch) => {
      const branchKey = branch?.id ?? branch?.name;
      if (!branchKey || seen.has(branchKey)) return;
      seen.add(branchKey);
      uniqueBranches.push(branch);
    });

    map.set(key, {
      ...existing,
      isActive: existing.isActive ?? item?.isActive,
      userRights: existing.userRights || item?.userRights,
      login: existing.login || item?.login,
      name: existing.name || item?.name,
      email: existing.email || item?.email,
      branches: uniqueBranches,
    });
  });

  return Array.from(map.values());
}

const Users = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [filters, setFilters] = useState({ isActive: null });
  const filtersRef = useRef({ isActive: null });
  const [appliedFilters, setAppliedFilters] = useState({ isActive: null });
  const [sortConfig, setSortConfig] = useState({ key: "Id", direction: "asc" });
  const [rememberedTotalCount, setRememberedTotalCount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [data, setData] = useState({
    items: [],
    itemsCount: null,
    totalRecords: 0,
    totalPages: 0,
    pageNumber: 1,
    pageSize: itemsPerPage,
  });

  const [fetchVersion, setFetchVersion] = useState(0);

  const currentPageRef = useRef(currentPage);
  const itemsPerPageRef = useRef(itemsPerPage);
  const appliedFiltersRef = useRef(appliedFilters);
  const sortConfigRef = useRef(sortConfig);
  const lastFetchedVersionRef = useRef(0);
  const abortControllerRef = useRef(null);

  const visibleItems = React.useMemo(() => aggregateUsers(data.items), [data.items]);

  filtersRef.current = filters;
  currentPageRef.current = currentPage;
  itemsPerPageRef.current = itemsPerPage;
  appliedFiltersRef.current = appliedFilters;
  sortConfigRef.current = sortConfig;

  React.useEffect(() => {
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  }, []);

  React.useEffect(() => {
    if (lastFetchedVersionRef.current === fetchVersion) return;
    lastFetchedVersionRef.current = fetchVersion;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    let cancelled = false;
    const pageNumber = currentPageRef.current;
    const pageSize = itemsPerPageRef.current;
    const filtersToApply = appliedFiltersRef.current || {};
    const sort = sortConfigRef.current || { key: "Id", direction: "asc" };

    const params = {
      PageNumber: pageNumber,
      PageSize: pageSize,
      SortBy: sort.key,
      SortDirection: sort.direction,
    };

    if (filtersToApply.isActive !== "" && filtersToApply.isActive !== undefined && filtersToApply.isActive !== null) {
      params.isActive = filtersToApply.isActive === "true";
    }

    if (filtersToApply.role) {
      params.Role = filtersToApply.role;
    }

    if (filtersToApply.branchId) {
      params.BranchId = Number(filtersToApply.branchId);
    }

    if (filtersToApply.query) {
      params.SearchForm = filtersToApply.query;
    }

    (async () => {
      setIsLoading(true);
      try {
        const response = await api.get("/User/List", {
          params,
          signal: controller.signal,
        });

        if (cancelled) return;

        const responsePageNumber = Number(response?.data?.pageNumber || pageNumber);
        const responseTotalCountRaw =
          response?.data?.totalCount ??
          response?.data?.itemsCount ??
          response?.data?.totalRecords;
        const responseTotalCount =
          Number.isFinite(Number(responseTotalCountRaw)) &&
          Number(responseTotalCountRaw) >= 0
            ? Number(responseTotalCountRaw)
            : null;

        if (responsePageNumber === 1) {
          setRememberedTotalCount(responseTotalCount);
        }

        setData({
          items: Array.isArray(response?.data?.items) ? response.data.items : [],
          itemsCount: responseTotalCount,
          totalRecords: response?.data?.totalRecords || 0,
          totalPages: response?.data?.totalPages || 0,
          pageNumber: response?.data?.pageNumber || pageNumber,
          pageSize: response?.data?.pageSize || pageSize,
        });
      } catch (error) {
        if (cancelled || error?.name === "CanceledError" || error?.name === "AbortError") return;
        setData({
          items: [],
          itemsCount: null,
          totalRecords: 0,
          totalPages: 0,
          pageNumber: 1,
          pageSize,
        });
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [fetchVersion]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const nextFilters = { ...prev, [key]: value };
      filtersRef.current = nextFilters;
      return nextFilters;
    });
  };

  const handleSearch = () => {
    const nextFilters = { ...filtersRef.current };
    setAppliedFilters(nextFilters);
    setCurrentPage(1);
    setRememberedTotalCount(null);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  const handleAdd = () => {
    navigate("/administration/users/add");
  };

  const handleSortChange = (nextSort) => {
    setSortConfig(nextSort);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  const handlePageChange = (page) => {
    if (Number(page) === Number(currentPageRef.current)) return;
    setCurrentPage(page);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  const handleItemsPerPageChange = (pageSize) => {
    setItemsPerPage(pageSize);
    setCurrentPage(1);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  return (
<div className="users-container">
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <img
              src={PeopleIcon}
              alt="People Icon"
              style={{ width: "30px", height: "30px" }}
            />
          </Col>
          <Col>
            <h3 style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
              Użytkownicy
            </h3>
          </Col>
          <Col xs="auto">
            <UsersButtons onSearch={handleSearch} onAdd={handleAdd} />
          </Col>
        </Row>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <UsersFilters filters={filters} onFilterChange={handleFilterChange} onSearch={handleSearch} />
          </Col>
        </Row>
        <Row>
          <Col>
            <UsersTable
              data={visibleItems}
              isLoading={isLoading}
              onSortChange={handleSortChange}
            />
          </Col>
        </Row>
        <Row className="align-items-center mt-4">
          <PaginationComponent
            itemsPerPage={data.pageSize}
            dataCount={visibleItems.length}
            itemsCount={data.itemsCount ?? rememberedTotalCount}
            totalCount={rememberedTotalCount}
            pageNumber={data.pageNumber}
            totalPages={data.totalPages}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemsPerPageOptions={[ 20, 100, 500]}
          />
        </Row>
      </Container>
    </div>
  )};

export default Users;